import type { UnwrapRef } from 'vue'
import { useToast } from 'vue-toastification'
import type { RemovableRef } from '@vueuse/shared'
import { StorageSerializers, useLocalStorage } from '@vueuse/core'
import type { User } from '~/model/interfaces/User'
import { useMyFetch } from '~/features/shared/useMyFetch'
import { Role } from '~/model/enums/Role'
import { getValidErrorMessage } from '~/helpers/getValidErrorMessage'

export const useUserStore = defineStore('user', {
    state: () => (<{
        user: RemovableRef<User | null>
        accessToken: RemovableRef<string | null | undefined>
        refreshToken: RemovableRef<string | null | undefined>
    }>{
        user: useLocalStorage('user', null, { serializer: StorageSerializers.object }),
        accessToken: useLocalStorage('accessToken', ''),
        refreshToken: useLocalStorage('refreshToken', ''),
    }),

    actions: {
        async login(form: { login: string, password: string } | UnwrapRef<{ password: string, email: string }>) {
            const {
                data,
                status,
            } = await useMyFetch<{ accessToken: string, refreshToken: string, user: User }>('user/login', {
                method: 'POST',
                body: unref(form),
            })

            if (unref(status) === 'success') {
                this.accessToken = unref(data)!.accessToken
                this.refreshToken = unref(data)!.refreshToken
                this.user = unref(data)!.user
                navigateTo('/', { external: true })
            } else {
                throw new Error('Неверный логин или пароль!')
            }
        },

        async setOnLine() {
            const toast = useToast()
            const form = {
                id: this.user!.id,
                onLine: !this.user!.onLine,
            }
            const {
                data,
                status,
                error,
            } = await useMyFetch<{ affected: number }>('user/setOnLine', {
                method: 'POST',
                body: unref(form),
            })

            if (unref(status) === 'success') {
                if (unref(data)!.affected > 0) {
                    this.user!.onLine = !this.user!.onLine
                }
            } else if (unref(error)) {
                toast.error(getValidErrorMessage(unref(error), 'Ошибка при смене статуса'))
            }
        },

        async logout() {
            const {
                status,
            } = await useMyFetch('user/logout', {
                method: 'POST',
            })

            if (unref(status) === 'success') {
                this.clearData()
            }
        },

        setTokens(tokens: {accessToken: string, refreshToken: string}) {
            this.accessToken = tokens.accessToken
            this.refreshToken = tokens.refreshToken
        },

        clearData() {
            this.accessToken = null
            this.refreshToken = null
            this.user = null
            navigateTo('/auth')
        },

        onlyForRoles(roles: Role[], item: any) {
            return [...roles, Role.DEVELOPER, Role.ADMIN, Role.SUPERADMIN].includes(this.role) ? [item] : []
        },
    },

    getters: {
        isAuthenticated: state => Boolean(state.accessToken),
        role: state => state.user?.roleId ?? Role.UNAUTHORIZED,
        isOnLine: state => !!state.user?.onLine,
    },

})
